import {
  boxShadow,
  paletteHelper,
  BadgeWithStatus,
  radiusHelper,
  spacingHelper,
  Text,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent, HTMLAttributeAnchorTarget } from 'react';
import styled, { css } from 'styled-components';
import { OutboundLink } from '../OutboundLink/OutboundLink';
import type {
  AerialImage,
  BadgeV2,
  ListingImage,
  TitleWithSourceLevel,
} from './PropertyTile.types';
import { isListingImage } from './PropertyTile.types';
import { ClickThroughSourceLocalStorageValue } from '../../../analytics/click-through-tracking/TrackingSource';
import { clickThroughSourceTracking } from '../../../analytics/click-through-tracking/clickThroughSourceTracking';
import { PcaLink } from '../PcaLink/PcaLink';
import { ScreenReaderTitle } from '../../helpers/screenReaderOnly';
import { PropertyFeature } from '../PropertyFeatures/subcomponents/PropertyFeature';
import { PropertyFeatures } from '../PropertyFeatures/PropertyFeatures';
import propertyImagePlaceHolderSvg from '../../../assets/property-image-placeholder.svg';

const badgeLeftTranslation = '12px';
const imageGradientOverlay = css`
  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 50%;
    display: inline-block;
    background: radial-gradient(101.45% 141.65% at 0% 175.38%, #000 0%, rgb(0 0 0 / 0%) 100%);
    border-radius: ${radiusHelper('medium')};
  }
`;

const Article = styled.article.attrs<{
  $containsAnchor?: boolean;
  'data-testid'?: 'property-tile';
}>(() => ({ 'data-testid': 'property-tile' }))`
  border-radius: ${radiusHelper('medium')};
  overflow: hidden;
  width: 100%;
  margin: ${spacingHelper('twoExtraSmall')};
  box-shadow:
    0 1px 2px 0 #26272b59,
    0 0 23px 0.5px #26272b0d;

  ${(props) =>
    props.$containsAnchor &&
    css`
      display: inline-block;
      text-decoration: none;

      &:hover {
        text-decoration: none;
        ${boxShadow.boxShadowHover};
      }
    `}
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  /* this background colour is for the loading state of the image */
  background-color: ${paletteHelper('backgroundHoveredPrimary')};
`;

const StyledBadge = styled(BadgeWithStatus)`
  transform: translate(${badgeLeftTranslation});
  margin-top: -12px;
`;

const Description = styled.div`
  padding: ${spacingHelper('extraSmall')} ${badgeLeftTranslation};
`;
const TitleWrapper = styled.div`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  line-height: 1.5rem;
`;

const TitleExtra = styled(Text)`
  ${typographyHelper('body02')}
  color: ${paletteHelper('textPrimary')};
  margin-left: ${spacingHelper('twoExtraSmall')};
  display: inline;
  align-items: center;
  /* Unicode middle dot: https://unicode-table.com/en/00B7/ */
  &::before {
    content: '\\B7';
    margin-right: ${spacingHelper('twoExtraSmall')};
  }
`;
const Title = styled(Text)`
  ${typographyHelper('body02Bold')}
  color: ${paletteHelper('textPrimary')};
  display: inline;
`;

const Subtitle = styled(Text)<{ showAvmData?: boolean }>`
  ${typographyHelper('caption')}
  color: ${paletteHelper('textSecondary')};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-top: ${({ showAvmData }) => (showAvmData ? spacingHelper('twoExtraSmall') : '0')};
  margin-bottom: ${spacingHelper('extraSmall')};
`;

const PropertyImage = styled(StyledImage)`
  border-radius: ${radiusHelper('medium')};
  display: block;
`;

const PropertyAerialImage = styled(StyledImage)`
  display: block;
  object-fit: cover;
`;

const PropertyImagePlaceHolderWrapper = styled.picture`
  display: block;

  aspect-ratio: 16 / 9;
  /* Fallback for aspect-ratio */
  @supports not (aspect-ratio: 16 / 9) {
    &::before {
      float: left;
      padding-top: 56.25%;
      content: '';
    }
    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
`;

const PropertyImagePlaceholder = styled(StyledImage)`
  display: block;
  object-fit: cover;
  background-color: ${paletteHelper('graphicGrey')};
`;

const PropertyImageWrapper = styled.picture`
  display: block;
  ${imageGradientOverlay};

  aspect-ratio: 16 / 9;

  /* Fallback for aspect-ratio */
  @supports not (aspect-ratio: 16 / 9) {
    &::before {
      float: left;
      padding-top: 56.25%;
      content: '';
    }
    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
`;

interface PropertyTileImageProps {
  image?: ListingImage | AerialImage | null;
  preloadImage?: boolean;
}

export const PropertyTileImage: FunctionComponent<PropertyTileImageProps> = ({
  image,
  preloadImage = false,
}) => {
  if (!image) {
    return (
      <PropertyImagePlaceHolderWrapper>
        <PropertyImagePlaceholder
          src={propertyImagePlaceHolderSvg}
          alt="Property image placeholder"
        />
      </PropertyImagePlaceHolderWrapper>
    );
  }

  if (isListingImage(image)) {
    return (
      <PropertyImageWrapper>
        <source srcSet={image.webpUri} />
        <PropertyImage
          src={image.jpgUri}
          loading={preloadImage ? 'eager' : 'lazy'}
          alt={image.alt}
        />
      </PropertyImageWrapper>
    );
  }

  return (
    <PropertyImageWrapper>
      <PropertyAerialImage
        src={image.uri}
        loading={preloadImage ? 'eager' : 'lazy'}
        alt={image.alt}
      />
    </PropertyImageWrapper>
  );
};

export interface PropertyTileProps {
  image?: ListingImage | AerialImage | null;
  badge: BadgeV2;
  title?: TitleWithSourceLevel | null;
  titleExtra?: string | null;
  subtitle: string;
  path?: string | null;
  clickThroughSource?: ClickThroughSourceLocalStorageValue | null;
  features?: PropertyFeature[] | null;
  className?: string;
  titleAs?: keyof HTMLElementTagNameMap;
  target?: HTMLAttributeAnchorTarget;
  preloadImage?: boolean;
}

const REAGroupLink = styled(OutboundLink)`
  &:hover {
    text-decoration: none;
  }

  &:focus {
    outline: -webkit-focus-ring-color auto 1px;
  }
`;

const reaLinkRex = /^https:\/\/www\.realestate\.com\.au.*/;

export const isReaGroupLink = (path: string): boolean => {
  return reaLinkRex.test(path);
};

export const PropertyTileV2: FunctionComponent<PropertyTileProps> = ({
  path,
  clickThroughSource,
  image,
  badge,
  title,
  titleAs = 'h3',
  target = '_self',
  titleExtra,
  subtitle,
  features,
  className,
  preloadImage,
}) => {
  const hoverTitle = [title?.display, titleExtra].filter(Boolean).join(' - ');
  const article = (
    <>
      <ScreenReaderTitle as={titleAs}>{subtitle}</ScreenReaderTitle>
      <PropertyTileImage image={image} preloadImage={preloadImage} />
      <StyledBadge variant={badge.variant}>{badge.text}</StyledBadge>
      <Description>
        <TitleWrapper>
          {title && (
            <Title data-source-level={title.sourceLevel} title={hoverTitle}>
              {title.display}
            </Title>
          )}
          {titleExtra && <TitleExtra>{titleExtra}</TitleExtra>}
        </TitleWrapper>
        <Subtitle title={subtitle}>{subtitle}</Subtitle>
        {features && <PropertyFeatures items={features} />}
      </Description>
    </>
  );

  if (path) {
    if (isReaGroupLink(path)) {
      return (
        <Article className={`property-tile ${className}`} $containsAnchor>
          <REAGroupLink href={path} outboundLinkType="srp-rea-link" data-testid="rea-group-link">
            {article}
          </REAGroupLink>
        </Article>
      );
    }
    return (
      <Article className={`property-tile ${className}`} $containsAnchor>
        <PcaLink
          anchorVariant="plain"
          onClick={() => clickThroughSourceTracking(clickThroughSource)}
          to={path}
          target={target}
          data-testid="pca-link"
        >
          {article}
        </PcaLink>
      </Article>
    );
  }
  return <Article className={className}>{article}</Article>;
};

export const CarouselTileV2 = styled(PropertyTileV2)`
  width: 14rem;
`;
